import { compararConPedidos } from '@/domain/planning';
import type { PedidoDeReceta } from '@/db/repos/orders';

/**
 * Avisa cuando lo cocinado no coincide con lo pedido.
 *
 * EL INCIDENTE QUE LE DA ORIGEN
 *
 * El 31 de agosto se cocino con el estimado del menu: 1 pollo, 2 chiles de carne
 * y 2 de queso. Los pedidos llegaron distintos: 3 de pollo, 0 de carne y 2 de
 * queso. La aplicacion mostraba los dos numeros en pantallas distintas, cada uno
 * correcto por su lado, y nunca dijo que se contradecian. Dos personas se habrian
 * quedado sin su pollo.
 *
 * Cocinar temprano y recibir pedidos despues es normal y no se va a evitar. Lo
 * que no puede pasar es que la aplicacion se quede callada.
 *
 * QUE AVISA Y CON QUE FUERZA
 *
 *   faltan  -> rojo. Alguien se queda sin su plato. Es el caso urgente.
 *   sobran  -> naranja. Hay comida sin dueño y su costo necesita destino.
 *   cuadra  -> verde, discreto. Confirmar que esta bien tambien sirve.
 *
 * Solo se muestra si ya se cocino. Antes de cocinar no hay desajuste que
 * reportar: el formulario de cocina ya propone las porciones pedidas.
 */

interface Props {
  /** Porciones que de verdad se cocinaron, o null si todavia no se cocina. */
  cocinadas: number | null;
  pedido: PedidoDeReceta | undefined;
}

export function AvisoDesajuste({ cocinadas, pedido }: Props) {
  if (cocinadas === null) return null;

  const pedidas = pedido?.porciones ?? 0;

  // Sin pedidos ni comida cocinada no hay nada que comparar.
  if (pedidas === 0 && cocinadas === 0) return null;

  const d = compararConPedidos(cocinadas, pedidas);

  if (d.cuadra) {
    return (
      <p className="mt-2 text-xs font-medium text-exito">
        ✓ Cocinaste justo lo pedido: {d.cocinadas}
      </p>
    );
  }

  if (d.faltan) {
    const faltantes = -d.diferencia;
    return (
      <p className="mt-2 text-sm font-semibold text-alerta">
        ⚠ Te pidieron {d.pedidas} y cocinaste {d.cocinadas}:{' '}
        {faltantes === 1 ? 'falta 1 porción' : `faltan ${faltantes} porciones`}
      </p>
    );
  }

  return (
    <p className="mt-2 text-sm font-semibold text-aviso">
      ⚠ Cocinaste {d.cocinadas} y{' '}
      {d.pedidas === 0 ? 'nadie lo pidió' : `solo te pidieron ${d.pedidas}`}:{' '}
      {d.diferencia === 1 ? 'sobra 1 porción' : `sobran ${d.diferencia} porciones`}
    </p>
  );
}
